var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { DialogHeader, DialogFooter, Alert, IconButton, useToast, DialogContent } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { AssistantChatKind } from 'shared/domains/apollo/generated/types';
import { FormDialog, SubmitButton, TextField, CancelButton } from 'shared/domains/forms';
import { useAccount } from 'shared/domains/users';
import { AddLabelAutoComplete } from './Fields/AddLabelAutoComplete';
import { ContentField } from './Fields/ContentField';
import { KindField } from './Fields/KindField';
import { PromptVisibility } from './Fields/PromptVisibility';
import { useGetLabels } from './useGetLabels';
import { useUpsertPrompt } from './useUpsertPrompt';
import { makeValidationSchema } from './validationSchema';
export function PromptFormDialog(props) {
    var _a, _b, _c, _d, _e, _f;
    const { onClose, initialValues } = props;
    const { data, error, loading } = useGetLabels();
    const { create, update, error: upsertPromptError } = useUpsertPrompt();
    const { addToast } = useToast();
    const schema = makeValidationSchema();
    const account = useAccount();
    const { role } = account.data || {};
    const isOwner = role === 'owner';
    const currentPromptId = initialValues === null || initialValues === void 0 ? void 0 : initialValues.id;
    const getTabularContent = (content) => {
        try {
            const parsed = JSON.parse(content);
            return parsed;
        }
        catch (_a) {
            return [];
        }
    };
    const formValues = {
        id: (_a = initialValues === null || initialValues === void 0 ? void 0 : initialValues.id) !== null && _a !== void 0 ? _a : '',
        name: (_b = initialValues === null || initialValues === void 0 ? void 0 : initialValues.name) !== null && _b !== void 0 ? _b : '',
        content: (_c = initialValues === null || initialValues === void 0 ? void 0 : initialValues.content) !== null && _c !== void 0 ? _c : '',
        isPublic: isOwner ? Boolean(initialValues === null || initialValues === void 0 ? void 0 : initialValues.isPublic) : false,
        labels: (_d = initialValues === null || initialValues === void 0 ? void 0 : initialValues.labels) !== null && _d !== void 0 ? _d : [],
        kind: (_e = initialValues === null || initialValues === void 0 ? void 0 : initialValues.kind) !== null && _e !== void 0 ? _e : AssistantChatKind.Regular,
        tabularContent: getTabularContent((_f = initialValues === null || initialValues === void 0 ? void 0 : initialValues.content) !== null && _f !== void 0 ? _f : ''),
    };
    const showKindField = Boolean(initialValues === null || initialValues === void 0 ? void 0 : initialValues.kind);
    const handleSubmit = (values) => __awaiter(this, void 0, void 0, function* () {
        const content = values.kind === AssistantChatKind.Tabular ? JSON.stringify(values.tabularContent) : values.content;
        try {
            if (values === null || values === void 0 ? void 0 : values.id) {
                yield update({
                    id: values.id,
                    input: {
                        name: values.name,
                        content,
                        isPublic: values.isPublic,
                        labels: values.labels,
                    },
                });
            }
            else {
                yield create({
                    input: {
                        name: values.name,
                        content,
                        isPublic: values.isPublic,
                        labels: values.labels,
                        kind: values.kind,
                    },
                });
            }
            addToast({
                title: t({
                    message: 'Prompt saved',
                    comment: 'Create prompt success toast',
                }),
                appearance: 'success',
            });
            onClose();
        }
        catch (err) {
            addToast({
                title: t({
                    message: 'Something went wrong, please try again later',
                    comment: 'Create prompt error toast',
                }),
                appearance: 'danger',
            });
        }
    });
    return (<FormDialog onClose={onClose} size="md" state={{ loading, error: Boolean(error) }} initialValues={formValues} onSubmit={handleSubmit} validationSchema={schema} validateOnChange={false}>
      <DialogHeader title={currentPromptId
            ? t({ message: 'Edit prompt', comment: 'Edit prompt dialog title' })
            : t({ message: 'Create prompt', comment: 'Create prompt dialog title' })} action={<IconButton appearance="ghost" $round onClick={onClose} aria-label={t({ message: 'Close', comment: 'close prompt edit dialog button' })}>
            <Falcon icon="xmark"/>
          </IconButton>}/>

      <DialogContent>
        {upsertPromptError && (<Alert appearance="danger" message={t({
                message: 'An error occurred.',
                comment: 'Create prompt error alert message',
            })}/>)}

        {!showKindField && <KindField />}

        <TextField autoComplete="off" name="name" label={t({ message: 'Name', comment: 'Prompt name field label' })} placeholder={t({
            message: 'Prompt name',
            comment: 'Prompt name field placeholder',
        })}/>

        <ContentField />

        <PromptVisibility disabled={!isOwner}/>

        <AddLabelAutoComplete suggestions={data}/>
      </DialogContent>

      <DialogFooter>
        <CancelButton onClick={onClose} size="small">
          <Trans comment="Upsert prompt cancel button">Cancel</Trans>
        </CancelButton>
        <SubmitButton size="small">
          <Trans comment="Upsert prompt save button">Save</Trans>
        </SubmitButton>
      </DialogFooter>
    </FormDialog>);
}
